import { postJSON } from 'common/services/api'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { useCallback } from 'react'
import { useIsMutating, useMutation, useQueryClient } from 'react-query'
import { YEAR_FIRST_DATE_FORMAT } from '../utils.datetime'
import {
  GENERATE_MUTATION_KEY,
  GENERATE_URL,
  REVISIONS_QUERY_KEY,
  ROTA_GENERATED_QUERY_KEY
} from './queryConstants'

dayjs.extend(isoWeek)

type UseGenerateReturn = {
  /**
   * Rejects if the request fails, so callers can keep the user on the form
   * instead of silently reporting success.
   */
  generateRota: (values: UseGeneratePayload) => Promise<unknown>
  /**
   * True while any generate request is in flight, anywhere in the tree. Backed
   * by the mutation cache rather than local state so it survives the calling
   * modal being unmounted and reopened.
   */
  isGenerating: boolean
}

type UseGenerateProps = {
  startDate: string
}

type UseGeneratePayload = {
  allocationTemplateName?: string
  allocationTemplateId?: string
}

const useGenerate = ({ startDate }: UseGenerateProps): UseGenerateReturn => {
  const queryClient = useQueryClient()

  // Generation takes several seconds. A second request that lands inside that
  // window creates a whole second rota for the week, duplicating every event
  // (ROST-3558), so callers must be able to see that one is already running.
  const inFlight = useIsMutating([GENERATE_MUTATION_KEY])

  const { mutateAsync } = useMutation(
    [GENERATE_MUTATION_KEY],
    (values: UseGeneratePayload) =>
      postJSON({
        url: `${GENERATE_URL}`,
        method: 'POST',
        isRota: true,
        json: {
          startDate: dayjs(startDate)
            .startOf('isoWeek')
            .format(YEAR_FIRST_DATE_FORMAT),
          name: values.allocationTemplateName,
          templateId: values.allocationTemplateId
        }
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([REVISIONS_QUERY_KEY])
        queryClient.invalidateQueries([ROTA_GENERATED_QUERY_KEY])
      }
    }
  )

  const generateRota = useCallback(
    async (values: UseGeneratePayload) => {
      // Read the mutation cache rather than the render-captured `inFlight`:
      // two calls in the same tick would both close over a stale zero and both
      // fire, which is the exact failure this hook exists to prevent.
      if (
        queryClient.isMutating({ mutationKey: [GENERATE_MUTATION_KEY] }) > 0
      ) {
        return undefined
      }

      return mutateAsync(values)
    },
    [queryClient, mutateAsync]
  )

  return {
    generateRota,
    isGenerating: inFlight > 0
  }
}

export default useGenerate
